import { useState } from "react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  CheckCircle2,
  Loader2,
  Wallet,
  TrendingUp,
  Mailbox,
  Filter,
  Zap,
  ShieldCheck,
  Building2,
  Home as HomeIcon,
} from "lucide-react";
import { api } from "@/lib/api";

const ASSET_TYPES = [
  "Single-Family",
  "Multi-Family (2-4)",
  "Multi-Family (5+)",
  "Condo / Townhome",
  "Mobile Home",
  "Land",
  "Commercial",
  "Note / Paper",
];

const FINANCING = [
  { value: "cash", label: "All cash" },
  { value: "hard_money", label: "Hard money" },
  { value: "dscr", label: "DSCR / conventional" },
  { value: "mixed", label: "Mixed / flexible" },
];

const REHAB = [
  { value: "turnkey", label: "Turnkey only" },
  { value: "light", label: "Light cosmetic" },
  { value: "medium", label: "Medium rehab" },
  { value: "full_gut", label: "Full gut / heavy" },
];

const VELOCITY = [
  { value: "1", label: "1 deal / month" },
  { value: "2_4", label: "2–4 deals / month" },
  { value: "5_9", label: "5–9 deals / month" },
  { value: "10_plus", label: "10+ deals / month" },
];

const VALUE_PROPS = [
  { icon: Mailbox, t: "Off-market deals first", d: "You see distressed sellers and pre-foreclosure properties before they list on the MLS — often before any other buyer." },
  { icon: Filter, t: "Buy-box matched", d: "We only send you deals that fit your markets, asset class, price, and rehab tolerance. No spam, no time-wasters." },
  { icon: Zap, t: "Fast-twitch dispo", d: "When we acquire a property, our buyer list gets the alert within hours. First serious offer takes the deal." },
  { icon: TrendingUp, t: "Volume that scales with you", d: "From 1 deal a month to 10+ — we adjust the flow to your appetite. No minimums, no contracts, no joining fees." },
  { icon: ShieldCheck, t: "Vetted titles, clean closings", d: "Every property comes with title checked, liens disclosed, and creative-financing options (subject-to, seller-finance, novations) where they apply." },
  { icon: Wallet, t: "100% free for buyers", d: "We make our money on the seller side. You get a deal flow that would normally cost $200+ per lead — at zero cost to you." },
];

export default function CashBuyers() {
  const [form, setForm] = useState({
    full_name: "",
    company: "",
    phone: "",
    email: "",
    target_markets: "",
    asset_types: ["Single-Family"],
    price_min: "",
    price_max: "",
    financing_type: "",
    rehab_tolerance: "",
    deals_per_month: "",
    message: "",
  });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const upd = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const toggleAsset = (a) =>
    setForm((f) => ({
      ...f,
      asset_types: f.asset_types.includes(a)
        ? f.asset_types.filter((x) => x !== a)
        : [...f.asset_types, a],
    }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.full_name || !form.phone || !form.email) {
      toast.error("Please fill in your name, phone, and email.");
      return;
    }
    if (form.asset_types.length === 0) {
      toast.error("Pick at least one asset type.");
      return;
    }
    setLoading(true);
    try {
      const payload = { ...form, source: "cash_buyers_section" };
      Object.keys(payload).forEach((k) => {
        if (payload[k] === "") payload[k] = null;
      });
      payload.full_name = form.full_name;
      payload.phone = form.phone;
      payload.email = form.email;
      payload.asset_types = form.asset_types;

      await api.post("/cash-buyers", payload);
      setDone(true);
      toast.success("You're on the list. Deals will hit your inbox soon.");
    } catch (err) {
      const detail = err?.response?.data?.detail || "Something went wrong. Please try again.";
      toast.error(typeof detail === "string" ? detail : "Submission failed.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section
      id="cash-buyers"
      data-testid="cash-buyers-section"
      className="py-20 md:py-32 bg-[#FAF9F6] relative overflow-hidden"
    >
      <div className="absolute -top-40 -right-40 h-96 w-96 rounded-full bg-[#C5A059]/10 blur-3xl pointer-events-none" />
      <div className="absolute -bottom-40 -left-40 h-96 w-96 rounded-full bg-[#2C5545]/10 blur-3xl pointer-events-none" />

      <div className="relative max-w-7xl mx-auto px-6 lg:px-8">
        <div className="max-w-3xl mb-12">
          <div className="mb-4">
            <span className="gold-rule" />
            <span className="text-xs uppercase tracking-[0.24em] text-[#2C5545] font-medium">
              For Cash Buyers & Investors
            </span>
          </div>
          <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl text-[#0B1B3D] leading-tight mb-5">
            Get off-market deals <em className="text-[#C5A059] not-italic font-medium">before they hit the MLS.</em>
          </h2>
          <p className="text-[#64748B] leading-relaxed text-base lg:text-lg">
            We talk to motivated sellers every day — pre-foreclosures, probate
            estates, tired landlords, distressed properties. Tell us your buy box
            and we'll send you the deals that match. Free, vetted, and as
            high-volume as you want.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-start">
          {/* Value props */}
          <div className="lg:col-span-6 grid grid-cols-1 sm:grid-cols-2 gap-5">
            {VALUE_PROPS.map((b, i) => (
              <div
                key={i}
                data-testid={`buyer-value-${i}`}
                className="rounded-lg border border-[#E5E3DB] bg-white p-6 hover:-translate-y-1 hover:shadow-[0_20px_50px_rgba(11,27,61,0.08)] transition-all"
              >
                <div className="h-10 w-10 rounded-md bg-[#0B1B3D]/5 border border-[#0B1B3D]/10 flex items-center justify-center mb-4">
                  <b.icon className="h-5 w-5 text-[#0B1B3D]" />
                </div>
                <h4 className="font-display text-lg text-[#0B1B3D] mb-2 leading-snug">{b.t}</h4>
                <p className="text-sm text-[#64748B] leading-relaxed">{b.d}</p>
              </div>
            ))}
          </div>

          {/* Form */}
          <div className="lg:col-span-6">
            {done ? (
              <div
                data-testid="buyer-form-success"
                className="rounded-lg bg-white border border-[#E5E3DB] p-10 text-center shadow-[0_20px_60px_rgba(11,27,61,0.08)]"
              >
                <CheckCircle2 className="h-12 w-12 text-[#2C5545] mx-auto mb-4" />
                <h3 className="font-display text-3xl text-[#0B1B3D] mb-3">
                  You're on the list.
                </h3>
                <p className="text-[#64748B] leading-relaxed">
                  We'll email matching deals as soon as they come in. For urgent
                  acquisitions, text <span className="font-medium text-[#0B1B3D]">706-506-3860</span>.
                </p>
              </div>
            ) : (
              <form
                onSubmit={submit}
                data-testid="cash-buyer-form"
                className="rounded-lg bg-white border border-[#E5E3DB] p-7 lg:p-8 shadow-[0_20px_60px_rgba(11,27,61,0.10)]"
              >
                <div className="flex items-center gap-2 mb-1">
                  <Wallet className="h-4 w-4 text-[#2C5545]" />
                  <span className="text-[11px] uppercase tracking-[0.22em] text-[#2C5545] font-medium">
                    Investor Signup — Free
                  </span>
                </div>
                <h3 className="font-display text-2xl lg:text-3xl text-[#0B1B3D] mb-1">
                  Tell us your buy box
                </h3>
                <p className="text-sm text-[#64748B] mb-6">
                  Takes 60 seconds. We only contact you when a deal matches.
                </p>

                <div className="grid grid-cols-1 gap-4">
                  <div>
                    <Label htmlFor="cb-name" className="text-xs uppercase tracking-[0.12em] text-[#0F172A]">
                      Full Name *
                    </Label>
                    <Input
                      id="cb-name"
                      data-testid="cb-full-name"
                      value={form.full_name}
                      onChange={(e) => upd("full_name", e.target.value)}
                      placeholder="Alex Carter"
                      className="mt-1.5 h-11"
                      required
                    />
                  </div>

                  <div>
                    <Label htmlFor="cb-company" className="text-xs uppercase tracking-[0.12em] text-[#0F172A]">
                      Company / Fund (optional)
                    </Label>
                    <Input
                      id="cb-company"
                      data-testid="cb-company"
                      value={form.company}
                      onChange={(e) => upd("company", e.target.value)}
                      placeholder="Carter Capital LLC"
                      className="mt-1.5 h-11"
                    />
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="cb-phone" className="text-xs uppercase tracking-[0.12em] text-[#0F172A]">
                        Phone *
                      </Label>
                      <Input
                        id="cb-phone"
                        data-testid="cb-phone"
                        type="tel"
                        value={form.phone}
                        onChange={(e) => upd("phone", e.target.value)}
                        placeholder="(555) 555-0110"
                        className="mt-1.5 h-11"
                        required
                      />
                    </div>
                    <div>
                      <Label htmlFor="cb-email" className="text-xs uppercase tracking-[0.12em] text-[#0F172A]">
                        Email *
                      </Label>
                      <Input
                        id="cb-email"
                        data-testid="cb-email"
                        type="email"
                        value={form.email}
                        onChange={(e) => upd("email", e.target.value)}
                        placeholder="alex@fund.com"
                        className="mt-1.5 h-11"
                        required
                      />
                    </div>
                  </div>

                  <div>
                    <Label htmlFor="cb-markets" className="text-xs uppercase tracking-[0.12em] text-[#0F172A]">
                      Target Markets / States
                    </Label>
                    <Input
                      id="cb-markets"
                      data-testid="cb-markets"
                      value={form.target_markets}
                      onChange={(e) => upd("target_markets", e.target.value)}
                      placeholder="GA, FL, TX or 30606, 30605 or Nationwide"
                      className="mt-1.5 h-11"
                    />
                  </div>

                  <div>
                    <Label className="text-xs uppercase tracking-[0.12em] text-[#0F172A]">
                      Asset Types *
                    </Label>
                    <div className="mt-2 grid grid-cols-2 gap-2">
                      {ASSET_TYPES.map((a) => {
                        const checked = form.asset_types.includes(a);
                        return (
                          <label
                            key={a}
                            data-testid={`cb-asset-${a.replace(/[^a-z0-9]+/gi, "-").toLowerCase()}`}
                            className={`flex items-center gap-2 rounded-md border px-3 py-2 cursor-pointer text-sm transition-colors ${
                              checked
                                ? "border-[#0B1B3D] bg-[#0B1B3D]/5 text-[#0B1B3D]"
                                : "border-[#E5E3DB] hover:border-[#0B1B3D]/40 text-[#64748B]"
                            }`}
                          >
                            <Checkbox
                              checked={checked}
                              onCheckedChange={() => toggleAsset(a)}
                            />
                            <span>{a}</span>
                          </label>
                        );
                      })}
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="cb-min" className="text-xs uppercase tracking-[0.12em] text-[#0F172A]">
                        Price Min
                      </Label>
                      <Input
                        id="cb-min"
                        data-testid="cb-price-min"
                        value={form.price_min}
                        onChange={(e) => upd("price_min", e.target.value)}
                        placeholder="$50,000"
                        className="mt-1.5 h-11"
                      />
                    </div>
                    <div>
                      <Label htmlFor="cb-max" className="text-xs uppercase tracking-[0.12em] text-[#0F172A]">
                        Price Max
                      </Label>
                      <Input
                        id="cb-max"
                        data-testid="cb-price-max"
                        value={form.price_max}
                        onChange={(e) => upd("price_max", e.target.value)}
                        placeholder="$400,000"
                        className="mt-1.5 h-11"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <Label className="text-xs uppercase tracking-[0.12em] text-[#0F172A]">
                        Financing
                      </Label>
                      <Select
                        value={form.financing_type}
                        onValueChange={(v) => upd("financing_type", v)}
                      >
                        <SelectTrigger className="mt-1.5 h-11" data-testid="cb-financing">
                          <SelectValue placeholder="Select..." />
                        </SelectTrigger>
                        <SelectContent>
                          {FINANCING.map((f) => (
                            <SelectItem key={f.value} value={f.value}>
                              {f.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label className="text-xs uppercase tracking-[0.12em] text-[#0F172A]">
                        Rehab Tolerance
                      </Label>
                      <Select
                        value={form.rehab_tolerance}
                        onValueChange={(v) => upd("rehab_tolerance", v)}
                      >
                        <SelectTrigger className="mt-1.5 h-11" data-testid="cb-rehab">
                          <SelectValue placeholder="Select..." />
                        </SelectTrigger>
                        <SelectContent>
                          {REHAB.map((r) => (
                            <SelectItem key={r.value} value={r.value}>
                              {r.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <div>
                    <Label className="text-xs uppercase tracking-[0.12em] text-[#0F172A]">
                      Deal Velocity
                    </Label>
                    <Select
                      value={form.deals_per_month}
                      onValueChange={(v) => upd("deals_per_month", v)}
                    >
                      <SelectTrigger className="mt-1.5 h-11" data-testid="cb-velocity">
                        <SelectValue placeholder="How many deals can you close per month?" />
                      </SelectTrigger>
                      <SelectContent>
                        {VELOCITY.map((v) => (
                          <SelectItem key={v.value} value={v.value}>
                            {v.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <Label htmlFor="cb-msg" className="text-xs uppercase tracking-[0.12em] text-[#0F172A]">
                      Anything specific you're hunting for?
                    </Label>
                    <Textarea
                      id="cb-msg"
                      data-testid="cb-message"
                      value={form.message}
                      onChange={(e) => upd("message", e.target.value)}
                      placeholder="Specific zip codes, year-built, bed/bath, off-market only, sub-to OK, etc."
                      className="mt-1.5 min-h-[80px]"
                    />
                  </div>

                  <button
                    type="submit"
                    disabled={loading}
                    data-testid="cb-submit"
                    className="mt-2 inline-flex items-center justify-center gap-2 w-full px-8 py-4 rounded-lg bg-[#C5A059] hover:bg-[#B38F4E] disabled:opacity-60 text-[#0B1B3D] font-semibold transition-all shadow-md hover:shadow-lg"
                  >
                    {loading ? (
                      <>
                        <Loader2 className="h-4 w-4 animate-spin" />
                        Adding you...
                      </>
                    ) : (
                      "Send Me Deals"
                    )}
                  </button>

                  <p className="text-[11px] text-[#64748B] text-center leading-relaxed mt-1">
                    Free. Unsubscribe anytime. We never share your buy box.
                  </p>
                </div>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
